import { NgModule } from '@angular/core'
import { RouterModule, Routes } from '@angular/router'
import { AppRoutesNS } from './app-routes.namespace'
import { AuthGuard } from './core/auth/auth.guard'
import { CustomPreloadingStrategy } from './core/custom-preloading.service'
import { MobileCustomerAccessGuard } from './core/mobile-customer-access.guard'
import { DeviceLockGuard } from './features/device/device-lock.guard'
import { OperationsGuard } from './features/operation/operations.guard'
import { PagesAuthRedirectGuard } from './features/pages/pages-auth-redirect.guard'
import { PendingSaleGuard } from './features/sale/pending-sale.guard'
import { AppShellLayoutComponent } from './layout/app-shell/app-shell-layout.component'
import { BrandedPageLayoutComponent } from './layout/branded-page/branded-page-layout.component'
import { environment } from 'src/environments/environment'

// We need this to redirect with all the query params
const redirectToScanRoute = (targetRoute: AppRoutesNS.Scan) => (route: any) => {
  const queryParams = new URLSearchParams()

  Object.entries(route.queryParams || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((entry) => queryParams.append(key, String(entry)))
      return
    }

    if (value !== null && value !== undefined) {
      queryParams.set(key, String(value))
    }
  })

  const queryString = queryParams.toString()
  return queryString ? `${targetRoute}?${queryString}` : targetRoute
}

const publicRoutes: Routes = [
  {
    path: 'oidc',
    loadChildren: () =>
      import('./core/auth/oidc/oidc.module').then((m) => m.OidcModule)
  },
  {
    path: 'public-discovery-schedule',
    loadChildren: () =>
      import(
        './features/pages/public-discovery-schedule/public-discovery-schedule.module'
      ).then((m) => m.PublicDiscoveryScheduleModule)
  },
  {
    path: 'public-discovery-rental',
    loadChildren: () =>
      import(
        './features/pages/public-discovery-rental/public-discovery-rental.module'
      ).then((m) => m.PublicDiscoveryRentalModule)
  },
  {
    path: 'public-discovery-courses',
    loadChildren: () =>
      import(
        './features/pages/public-discovery-courses/public-discovery-courses.module'
      ).then((m) => m.PublicDiscoveryCoursesModule)
  },
  {
    path: 'scan',
    loadChildren: () =>
      import('./features/pages/scan/scan.module').then((m) => m.ScanModule)
  }
]

const publicScanRoutes: Routes = [
  // We want to conserve the old route for scans at least for V13 upgrade
  {
    path: AppRoutesNS.Scan.LEGACY_REDIRECT.slice(1),
    redirectTo: redirectToScanRoute(AppRoutesNS.Scan.REDIRECT),
    pathMatch: 'full'
  }
]

const brandedRoutes: Routes = [
  {
    path: 'pages',
    component: BrandedPageLayoutComponent,
    canActivate: [MobileCustomerAccessGuard, PagesAuthRedirectGuard],
    loadChildren: () =>
      import('./features/pages/pages.module').then((m) => m.PagesModule)
  },
  {
    path: 'operations',
    component: BrandedPageLayoutComponent,
    canActivate: [MobileCustomerAccessGuard, AuthGuard, DeviceLockGuard],
    loadChildren: () =>
      import('./features/operation/operation.module').then(
        (m) => m.OperationModule
      )
  }
]

const appContentRoutes: Routes = [
  {
    path: 'home',
    loadChildren: () =>
      import('./features/home/home.module').then((m) => m.HomeModule)
  },
  {
    path: 'public-subscription',
    loadChildren: () =>
      import(
        './features/pages/public-subscriptions/public-subscription.module'
      ).then((m) => m.PublicSubscriptionModule),
    data: { layout: { showFooter: false } }
  },
  {
    path: 'public-schedule',
    loadChildren: () =>
      import(
        './features/pages/public-discovery-schedule/public-discovery-schedule.module'
      ).then((m) => m.PublicDiscoveryScheduleModule)
  },
  {
    path: AppRoutesNS.Upcoming.UPCOMING_BOOKINGS,
    redirectTo: 'upcoming',
    pathMatch: 'full'
  },
  {
    path: 'upcoming',
    canActivate: [
      MobileCustomerAccessGuard,
      AuthGuard,
      OperationsGuard,
      DeviceLockGuard,
      PendingSaleGuard
    ],
    loadChildren: () =>
      import('./features/upcoming/upcoming.module').then(
        (m) => m.UpcomingModule
      ),
    data: { preload: true }
  },
  {
    path: 'shop',
    canActivate: [
      AuthGuard,
      MobileCustomerAccessGuard,
      PendingSaleGuard,
      DeviceLockGuard
    ],
    canActivateChild: [MobileCustomerAccessGuard],
    loadChildren: () =>
      import('./features/shop/shop.module').then((m) => m.ShopModule),
    data: { preload: true }
  },
  {
    path: 'sales',
    canActivate: [MobileCustomerAccessGuard, AuthGuard, DeviceLockGuard],
    loadChildren: () =>
      import('./features/sale/sale.module').then((m) => m.SaleModule)
  },
  {
    path: 'members',
    canActivate: [MobileCustomerAccessGuard, AuthGuard, OperationsGuard],
    loadChildren: () =>
      import('./features/member/member.module').then((m) => m.MemberModule)
  },
  {
    path: 'payments',
    canActivate: [
      MobileCustomerAccessGuard,
      AuthGuard,
      OperationsGuard,
      DeviceLockGuard
    ],
    loadChildren: () =>
      import('./features/sale/payment/payment.module').then(
        (m) => m.PaymentModule
      )
  },
  {
    path: 'cart',
    canActivate: [
      MobileCustomerAccessGuard,
      AuthGuard,
      PendingSaleGuard,
      DeviceLockGuard
    ],
    loadChildren: () =>
      import('./features/cart/cart.module').then((m) => m.CartModule),
    data: { layout: { hidePendingSaleBlock: true, hideCartButton: true } }
  },
  {
    path: 'organizations',
    canActivate: [
      MobileCustomerAccessGuard,
      AuthGuard,
      OperationsGuard,
      DeviceLockGuard,
      PendingSaleGuard
    ],
    loadChildren: () =>
      import('./features/organization/organization.module').then(
        (m) => m.OrganizationModule
      )
  },
  {
    path: 'my-work',
    canActivate: [
      MobileCustomerAccessGuard,
      AuthGuard,
      OperationsGuard,
      DeviceLockGuard,
      PendingSaleGuard
    ],
    loadChildren: () =>
      import('./features/my-work/my-work.module').then((m) => m.MyWorkModule)
  },
  {
    path: 'competitions',
    canActivate: [
      MobileCustomerAccessGuard,
      AuthGuard,
      OperationsGuard,
      DeviceLockGuard,
      PendingSaleGuard
    ],
    loadChildren: () =>
      import('./features/competition/competition.module').then(
        (m) => m.CompetitionModule
      )
  },
  {
    path: 'competition-team',
    canActivate: [
      MobileCustomerAccessGuard,
      AuthGuard,
      OperationsGuard,
      DeviceLockGuard,
      PendingSaleGuard
    ],
    loadChildren: () =>
      import(
        './features/competition/competition-team/competition-team.module'
      ).then((m) => m.CompetitionTeamModule)
  },
  {
    path: 'welcome',
    canActivate: [MobileCustomerAccessGuard, AuthGuard, PendingSaleGuard],
    loadChildren: () =>
      import('./features/welcome/welcome.module').then((m) => m.WelcomeModule)
  },
  {
    path: 'bookings',
    canActivate: [AuthGuard, MobileCustomerAccessGuard, PendingSaleGuard],
    loadChildren: () =>
      import('./features/booking/booking.module').then((m) => m.BookingModule),
    data: { preload: true }
  },
  {
    path: 'overview',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('./features/overview/overview.module').then(
        (m) => m.OverviewModule
      ),
    data: { preload: true }
  },
  {
    path: '',
    redirectTo: environment.unauthenticatedShopping
      ? environment.unauthenticatedShoppingRedirect
      : AppRoutesNS.Pages.LOGIN,
    pathMatch: 'full'
  }
]

const routes: Routes = [
  ...publicRoutes,
  ...publicScanRoutes,
  ...brandedRoutes,
  // Main application shell: persistent navbar/footer. Child routes choose their content layout.
  {
    path: '',
    component: AppShellLayoutComponent,
    children: appContentRoutes
  },
  {
    path: '**',
    redirectTo: environment.moduleConfigs.authModule.afterLoginSuccessRedirect
  }
]

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      onSameUrlNavigation: 'reload',
      paramsInheritanceStrategy: 'always',
      scrollPositionRestoration: 'enabled',
      preloadingStrategy: CustomPreloadingStrategy
    })
  ],
  exports: [RouterModule]
})
export class AppRoutingModule {}
